import {
  ConfiguracionDeSeguridadSchema,
  ListaDeUsuariosSchema,
  PaginaDeBitacoraSchema,
  PreparacionDeDosPasosSchema,
  ResultadoDeDeshacerSchema,
  ResultadoDeIngresoSchema,
  ResultadoSimpleSchema,
  SesionActualSchema,
  UsuarioActualSchema,
  UsuarioConContrasenaTemporalSchema,
  UsuarioSchema,
  type Accesos,
  type CambioDeContrasena,
  type ConfiguracionDeSeguridad,
  type ConsultaDeBitacora,
  type EdicionDeUsuario,
  type Ingreso,
  type NuevoUsuario,
  type Preferencias,
  type Restablecimiento,
  type SesionActual,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {ErrorDeApi, json, pedir} from './cliente';
import {refrescarDatos} from './refrescar';

export const CLAVE_SESION = ['sesion', 'actual'] as const;

// La sesion actual; null cuando no hay (401), sin reintentos.
export function useSesionActual() {
  return useQuery<SesionActual | null, ErrorDeApi>({
    queryKey: CLAVE_SESION,
    queryFn: async () => {
      try {
        return await pedir('sesion/actual', SesionActualSchema);
      } catch (error) {
        if (error instanceof ErrorDeApi && error.codigo === 'NO_AUTORIZADO') return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useIngresar() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: Ingreso) => pedir('sesion/ingresar', ResultadoDeIngresoSchema, json('POST', datos)),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: CLAVE_SESION});
    },
  });
}

export function useDosPasos() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (codigo: string) => pedir('sesion/dos-pasos', ResultadoDeIngresoSchema, json('POST', {codigo})),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: CLAVE_SESION});
    },
  });
}

export function useSalir() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => pedir('sesion/salir', ResultadoSimpleSchema, json('POST')),
    onSettled: async () => {
      cliente.clear();
      await cliente.invalidateQueries({queryKey: CLAVE_SESION});
    },
  });
}

// ── Mi cuenta ───────────────────────────────────────────────────────────────

function useCambioDeCuenta<T, R>(mutationFn: (datos: T) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: CLAVE_SESION});
    },
  });
}

export const useCambiarContrasena = () =>
  useCambioDeCuenta((datos: CambioDeContrasena) => pedir('cuenta/contrasena', UsuarioActualSchema, json('PUT', datos)));

export function usePrepararDosPasos() {
  return useMutation({
    mutationFn: () => pedir('cuenta/dos-pasos/preparar', PreparacionDeDosPasosSchema, json('POST')),
  });
}

export const useActivarDosPasos = () =>
  useCambioDeCuenta((codigo: string) => pedir('cuenta/dos-pasos/activar', UsuarioActualSchema, json('POST', {codigo})));

export const useDesactivarDosPasos = () =>
  useCambioDeCuenta((codigo: string) => pedir('cuenta/dos-pasos', UsuarioActualSchema, json('DELETE', {codigo})));

export const useGuardarPreferencias = () =>
  useCambioDeCuenta((datos: Preferencias) => pedir('cuenta/preferencias', UsuarioActualSchema, json('PUT', datos)));

// ── Administracion ──────────────────────────────────────────────────────────

export function useUsuarios() {
  return useQuery({
    queryKey: ['administracion', 'usuarios'],
    queryFn: () => pedir('administracion/usuarios', ListaDeUsuariosSchema),
  });
}

function useCambioDeUsuarios<T, R>(mutationFn: (datos: T) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['administracion']});
    },
  });
}

export const useCrearUsuario = () =>
  useCambioDeUsuarios((datos: NuevoUsuario) =>
    pedir('administracion/usuarios', UsuarioConContrasenaTemporalSchema, json('POST', datos)),
  );

export const useEditarUsuario = () =>
  useCambioDeUsuarios(({id, cambios}: {id: number; cambios: EdicionDeUsuario}) =>
    pedir(`administracion/usuarios/${id}`, UsuarioSchema, json('PATCH', cambios)),
  );

// Secciones y pestanas de una persona (decision 0062); null = todo lo de su rol.
export const useEditarAccesos = () =>
  useCambioDeUsuarios(({id, accesos}: {id: number; accesos: Accesos | null}) =>
    pedir(`administracion/usuarios/${id}/accesos`, UsuarioSchema, json('PUT', {accesos})),
  );

export const useRestablecerContrasena = () =>
  useCambioDeUsuarios(({id, opciones}: {id: number; opciones: Restablecimiento}) =>
    pedir(`administracion/usuarios/${id}/restablecer`, UsuarioConContrasenaTemporalSchema, json('POST', opciones)),
  );

// Deshacer una entrada de la bitacora: revisar dice que vuelve a como estaba; deshacer lo hace.
export function useDeshacer() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({id, modo}: {id: number; modo: 'revisar' | 'deshacer'}) =>
      pedir(`administracion/bitacora/${id}/deshacer`, ResultadoDeDeshacerSchema, json('POST', {modo})),
    onSuccess: async r => {
      if (r.hecho) await refrescarDatos(cliente);
    },
  });
}

export function useBitacora(consulta: Partial<ConsultaDeBitacora>) {
  const parametros = new URLSearchParams();
  for (const [clave, valor] of Object.entries(consulta)) {
    if (valor !== undefined && valor !== '' && valor !== null) parametros.set(clave, String(valor));
  }
  const cadena = parametros.toString();
  return useQuery({
    queryKey: ['administracion', 'bitacora', cadena],
    queryFn: () => pedir(`administracion/bitacora${cadena ? `?${cadena}` : ''}`, PaginaDeBitacoraSchema),
    placeholderData: keepPreviousData,
  });
}

export function useConfiguracionDeSeguridad() {
  return useQuery({
    queryKey: ['administracion', 'configuracion'],
    queryFn: () => pedir('administracion/configuracion', ConfiguracionDeSeguridadSchema),
  });
}

export function useGuardarConfiguracion() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: ConfiguracionDeSeguridad) =>
      pedir('administracion/configuracion', ConfiguracionDeSeguridadSchema, json('PUT', datos)),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['administracion', 'configuracion']});
      await cliente.invalidateQueries({queryKey: CLAVE_SESION});
    },
  });
}
